import { type SpeisenGetraenke as Bild } from '../api'
import { anzahl, euro, menge, prozent } from '../format'
import { Anteile } from './Anteile'
import { Karte } from './Karte'

/* Speisen und Getränke.
 *
 * Die Frage, die jeder Wirt zuerst stellt — und die bis zur
 * Artikelliste nicht zu beantworten war, weil an der Warengruppe fast
 * nie steht, was sie ist. Am Artikel steht es immer: die Oberebene
 * seiner Statistikgruppe.
 *
 * Zähler und Nenner kommen getrennt aus der API und werden erst hier
 * geteilt. Eine Quote aus zwei Grundgesamtheiten ist keine.
 */

const NAME: Record<string, string> = {
  FOOD: 'Speisen',
  BEVERAGE: 'Getränke',
  OTHER: 'Sonstiges',
  UNKNOWN: 'nicht zugeordnet',
}

export function SpeisenGetraenke({ d }: { d: Bild }) {
  const gesamt = Number(d.umsatz)
  if (!d.arten.length || !gesamt) {
    return (
      <Karte titel="Speisen und Getränke">
        <div className="leer">Keine Umsätze an diesem Tag.</div>
      </Karte>
    )
  }

  const speisen = d.arten.find((a) => a.art === 'FOOD')
  const getraenke = d.arten.find((a) => a.art === 'BEVERAGE')
  // Das Verhältnis, das der Standort steuert: Getränke gegen Speisen.
  // Sonstiges und Nichtzugeordnetes gehören in keinen der beiden Töpfe
  // und deshalb auch nicht in den Nenner.
  const kern = Number(speisen?.umsatz ?? 0) + Number(getraenke?.umsatz ?? 0)
  const getraenkequote = kern ? (Number(getraenke?.umsatz ?? 0) / kern) * 100 : null

  return (
    <Karte titel="Speisen und Getränke"
           neben="netto nach Storno, ohne Trinkgeld und Rabatt">
      <Anteile
        anteile={d.arten.map((a) => ({
          name: NAME[a.art] ?? a.art,
          wert: Number(a.umsatz),
          anzahl: a.positionen,
        }))}
        formatiere={euro} anzahlName="Positionen" />

      <table style={{ marginTop: 16 }}>
        <thead>
          <tr>
            <th>Art</th>
            <th className="r">Umsatz</th>
            <th className="r">Anteil</th>
            <th className="r">Menge</th>
            <th className="r">auf Tischbelegungen</th>
          </tr>
        </thead>
        <tbody>
          {d.arten.map((a) => (
            <tr key={a.art}>
              <td>{NAME[a.art] ?? a.art}</td>
              <td className="r zahl">{euro(a.umsatz)}</td>
              <td className="r zahl dezent">
                {prozent((Number(a.umsatz) / gesamt) * 100)}
              </td>
              <td className="r zahl dezent">{menge(a.menge)}</td>
              <td className="r zahl">
                {a.tischbesuche === null || !d.tischbesuche_gesamt ? '–' : (
                  <>
                    <span className="dezent">{anzahl(a.tischbesuche)}</span>
                    <span className="dezent">{' · '}</span>
                    <span style={{ marginLeft: 9, minWidth: 46,
                                   display: 'inline-block' }}>
                      {prozent((a.tischbesuche / d.tischbesuche_gesamt) * 100, 0)}
                    </span>
                  </>
                )}
              </td>
            </tr>
          ))}
          <tr>
            <td><b>Artikelumsatz</b></td>
            <td className="r zahl"><b>{euro(d.umsatz)}</b></td>
            <td className="r zahl dezent">100 %</td>
            <td />
            <td className="r zahl">
              {d.tischbesuche_gesamt
                ? <span className="dezent">{anzahl(d.tischbesuche_gesamt)}</span>
                : '–'}
            </td>
          </tr>
        </tbody>
      </table>

      {getraenkequote !== null && (
        <p className="merker" style={{ marginTop: 14 }}>
          Getränkeanteil <b className="zahl">{prozent(getraenkequote)}</b> vom
          Umsatz aus Speisen und Getränken
          {d.tischbesuche_gesamt && getraenke?.tischbesuche
            ? `. Auf ${prozent((getraenke.tischbesuche
                / d.tischbesuche_gesamt) * 100, 0)} der `
              + `${anzahl(d.tischbesuche_gesamt)} Tischbelegungen stand `
              + 'mindestens ein Getränk'
            : ''}.
        </p>
      )}

      {d.ohne_zuordnung !== null && (
        <p className="dezent" style={{
          fontSize: 11, marginTop: 10, marginBottom: 0, lineHeight: 1.6,
        }}>
          <b className="zahl">{euro(d.ohne_zuordnung)}</b> sind weder
          Speise noch Getränk zugeordnet — diesen Artikeln fehlt die
          Statistikgruppe. Geraten wird nichts; welche es sind, steht in
          der Einrichtung.
        </p>
      )}
    </Karte>
  )
}
